import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { approvals as approvalsApi } from '../../lib/api';
import {
  Loader2, Check, X, ExternalLink, Calendar, FileText, Image as ImageIcon,
  Video as VideoIcon, BarChart2, Globe, Users, AlertTriangle,
} from 'lucide-react';

/**
 * Public landing for emailed approval links.
 * URL: /approve/:token  (no auth required — token is the credential)
 */
export default function ApproveByToken() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {status, url, error?}
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await approvalsApi.fetchByToken(token);
        if (cancel) return;
        setDoc(d);
      } catch (e) {
        if (!cancel) setError(e.message || 'Invalid or expired link.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const decide = async (decision) => {
    if (decision === 'reject' && !rejectComment.trim()) {
      setError('Please add a short comment describing what should change.');
      return;
    }
    setBusy(true);
    try {
      const res = await approvalsApi.decideByToken(token, { decision, rejection_comment: rejectComment });
      setDone(res);
    } catch (e) {
      setError(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBFA]">
        <Loader2 className="w-7 h-7 animate-spin text-[#6B6B6B]" />
      </div>
    );
  }
  if (error && !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBFA] p-6">
        <div className="bg-white border border-[#E3E2E0] rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-[#CE3636] mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-2">Link not valid</h1>
          <p className="text-sm text-[#6B6B6B]">{error}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBFA] p-6">
        <div className="bg-white border border-[#E3E2E0] rounded-2xl p-8 max-w-md text-center">
          {done.status === 'rejected' ? (
            <>
              <div className="w-12 h-12 rounded-full bg-[#FFEBE9] flex items-center justify-center mx-auto mb-3">
                <X className="w-6 h-6 text-[#CE3636]" />
              </div>
              <h1 className="text-lg font-semibold mb-2">Post rejected</h1>
              <p className="text-sm text-[#6B6B6B]">Your team has been notified.</p>
            </>
          ) : done.status === 'published' ? (
            <>
              <div className="w-12 h-12 rounded-full bg-[#E6F4EA] flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-[#1E7F4E]" />
              </div>
              <h1 className="text-lg font-semibold mb-2">Posted to LinkedIn 🎉</h1>
              {done.url && (
                <a href={done.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#0052CC] hover:underline">
                  <ExternalLink className="w-3.5 h-3.5" /> View post
                </a>
              )}
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-[#EAF2FB] flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-[#0052CC]" />
              </div>
              <h1 className="text-lg font-semibold mb-2">Approved & scheduled</h1>
              <p className="text-sm text-[#6B6B6B]">
                We'll publish it automatically at the scheduled time.
                {done.publish_failed && <><br /><span className="text-[#CE3636]">Note: a publishing error occurred — your team has been notified.</span></>}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const TypeIcon = (() => {
    if (doc.post_type === 'photo') return ImageIcon;
    if (doc.post_type === 'video') return VideoIcon;
    if (doc.post_type === 'poll') return BarChart2;
    return FileText;
  })();
  const VisIcon = doc.visibility === 'connections' ? Users : Globe;

  return (
    <div className="min-h-screen bg-[#FBFBFA] p-6">
      <div className="max-w-xl mx-auto">
        <header className="mb-4 text-center">
          <h1 className="text-xl font-semibold text-[#191919]">Approve LinkedIn post</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {doc.created_by_admin_email} drafted this on behalf of {doc.target_name || doc.target_email}.
          </p>
          {doc.status !== 'pending' && (
            <p className="text-xs text-[#CE3636] mt-2">
              This post has already been {doc.status}.
            </p>
          )}
        </header>

        <article className="bg-white border border-[#E3E2E0] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-[#F0E6FF] text-[#5A3EA8] font-medium">
              <TypeIcon className="w-3 h-3" /> {doc.post_type}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-[#6B6B6B]">
              <VisIcon className="w-3 h-3" /> {doc.visibility === 'connections' ? 'Connections' : 'Anyone'}
            </span>
            {doc.scheduled_at && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[#0052CC] bg-[#EAF2FB] px-2 py-0.5 rounded-md ml-auto">
                <Calendar className="w-3 h-3" /> {new Date(doc.scheduled_at).toLocaleString()}
              </span>
            )}
          </div>

          {doc.article_title && (
            <h2 className="text-lg font-bold text-[#191919] mb-2">{doc.article_title}</h2>
          )}

          <p className="text-sm text-[#191919] whitespace-pre-wrap p-3 bg-[#FBFBFA] rounded-lg border border-[#E3E2E0]">
            {doc.text}
          </p>

          {doc.link_url && (
            <a href={doc.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#0052CC] hover:underline">
              <ExternalLink className="w-3 h-3" /> {doc.link_url}
            </a>
          )}

          {doc.post_type === 'poll' && doc.poll_options && (
            <div className="mt-3 border border-[#E3E2E0] rounded-lg p-3 bg-white">
              <p className="text-[11px] text-[#6B6B6B] mb-2">Poll · {doc.poll_duration_days || 7}-day duration</p>
              {doc.poll_options.map((opt, i) => (
                <div key={i} className="text-sm text-[#191919] py-1.5 px-3 border border-[#E3E2E0] rounded-md mb-1.5">
                  {opt}
                </div>
              ))}
            </div>
          )}
        </article>

        {error && (
          <div className="mt-3 text-sm text-[#CE3636] flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {doc.status === 'pending' && (
          <>
          {rejectMode && (
            <div className="mt-4 bg-white border border-[#E3E2E0] rounded-xl p-3">
              <label className="block text-xs font-medium text-[#191919] mb-1.5">
                What should your team change?
              </label>
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={4}
                className="w-full bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/10"
                placeholder="Example: Please change the image, shorten the copy, or use a different tone."
                data-testid="email-reject-comment"
              />
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 mt-4 justify-end">
            <button
              onClick={() => {
                if (!rejectMode) {
                  setRejectMode(true);
                  setError(null);
                } else {
                  decide('reject');
                }
              }}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 text-[#CE3636] border border-[#FF8080] hover:bg-[#FFEBE9] rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="email-reject"
            >
              <X className="w-4 h-4" /> {rejectMode ? 'Send request' : 'Request changes'}
            </button>
            <button
              onClick={() => decide('approve')}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-[#1E7F4E] hover:bg-[#176A40] text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              data-testid="email-approve"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {doc.scheduled_at ? 'Approve · schedule' : 'Approve & publish'}
            </button>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
